/**
 * CloudFront 리사이즈 이미지 URL
 *
 *   원본   https://<cdn>/image/{uuid}.{ext}
 *   thumb  https://<cdn>/image/resized/thumb/{uuid}.webp    (300×300)
 *   medium https://<cdn>/image/resized/medium/{uuid}.webp   (800×800)
 *
 * 파일 API는 thumbUrl·mediumUrl을 직접 내려주지만, 파트너·배우처럼 원본 URL만
 * 담고 있는 응답이 많다. 규칙이 단순하므로 원본에서 유도해 쓴다.
 *
 * 리사이즈는 Lambda 비동기 처리라 업로드 직후에는 아직 없을 수 있다.
 * FadeImage가 리사이즈 → 원본 → 기본 이미지 순으로 물러나므로 그 사이도 안전하다.
 */

export const IMAGE_SIZES = ['thumb', 'medium'];

/**
 * @param {string} url  원본 이미지 URL
 * @param {'thumb'|'medium'} size
 * @returns {string|null} 규칙에 맞지 않으면 null (외부 이미지·data URI·이미 리사이즈된 URL)
 */
/**
 * 이미지 URL을 뜯어 { prefix, uuid, ext, size } 로 만든다.
 * 서버가 원본 URL을 줄 때도, 이미 리사이즈된 URL을 줄 때도 모두 대상이다.
 */
function parseImageUrl(url) {
  if (!url || typeof url !== 'string') return null;
  if (url.startsWith('data:') || url.startsWith('blob:')) return null;
  try {
    const u = new URL(url, window.location.origin);

    // 이미 리사이즈된 URL — /image/resized/{size}/{uuid}.webp
    const resized = u.pathname.match(/^(.*)\/image\/resized\/([^/]+)\/([^/]+)\.[^./]+$/);
    if (resized) return { url: u, prefix: resized[1], size: resized[2], uuid: resized[3] };

    // 원본 — /image/{uuid}.{ext}
    const origin = u.pathname.match(/^(.*)\/image\/([^/]+)\.([^./]+)$/);
    if (origin) return { url: u, prefix: origin[1], size: null, uuid: origin[2], ext: origin[3] };

    return null;
  } catch {
    return null;
  }
}

export function resizedImageUrl(url, size = 'medium') {
  if (!IMAGE_SIZES.includes(size)) return null;
  const parsed = parseImageUrl(url);
  if (!parsed) return null;
  if (parsed.size === size) return null;   // 이미 그 크기면 바꿀 것이 없다

  parsed.url.pathname = `${parsed.prefix}/image/resized/${size}/${parsed.uuid}.webp`;
  parsed.url.search = '';
  return parsed.url.toString();
}

/**
 * 리사이즈본에서 원본 URL을 되돌린다.
 *
 * 리사이즈 webp는 손실 VP8이라 투명 배경이 사라진다(검정으로 깔린다).
 * 로고처럼 투명이 중요한 이미지는 원본을 먼저 시도해야 한다.
 * 원본 확장자는 URL에 남지 않으므로 넘겨받은 후보를 쓴다.
 */
export function originalImageUrl(url, ext = 'png') {
  const parsed = parseImageUrl(url);
  if (!parsed) return null;
  if (parsed.size === null) return null;   // 이미 원본이다

  parsed.url.pathname = `${parsed.prefix}/image/${parsed.uuid}.${ext}`;
  parsed.url.search = '';
  return parsed.url.toString();
}
